import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { QuickAssessmentData, QuickAssessmentResult } from '../../services/smart-matching.service'
import { useQuickAssessment } from '../../hooks/useSmartMatching'
import LoadingSpinner from '../LoadingSpinner'

// Validation schema for quick assessment
const quickAssessmentSchema = z.object({
  urgencyLevel: z.enum(['IMMEDIATE', 'TODAY', 'TOMORROW'], {
    required_error: 'Please select the urgency level',
  }),
  locationType: z.enum(['HOSPITAL', 'HOME', 'MOSQUE', 'OTHER'], {
    required_error: 'Please select the location type',
  }),
  currentLocation: z.string().min(3, 'Please enter the current location'),
  preferredBurialArea: z.string().min(3, 'Please enter the preferred burial area'),
})

type QuickAssessmentFormData = z.infer<typeof quickAssessmentSchema>

interface QuickAssessmentStepProps {
  onNext: (assessmentResult: QuickAssessmentResult) => void
  onBack?: () => void
  initialData?: Partial<QuickAssessmentData>
}

const urgencyDescriptions = {
  IMMEDIATE: {
    title: 'Immediate (Within 3 hours)',
    description: 'Critical situation requiring immediate attention',
    icon: '🚨',
    color: 'text-red-600 border-red-200 bg-red-50',
  },
  TODAY: {
    title: 'Today (Within 12 hours)',
    description: 'Urgent but can be planned for today',
    icon: '⏰',
    color: 'text-orange-600 border-orange-200 bg-orange-50',
  },
  TOMORROW: {
    title: 'Tomorrow (Within 24 hours)',
    description: 'Can be planned with more time',
    icon: '📅',
    color: 'text-blue-600 border-blue-200 bg-blue-50',
  },
}

const locationTypeDescriptions = {
  HOSPITAL: {
    title: 'Hospital',
    description: 'Deceased is currently at a hospital',
    icon: '🏥',
  },
  HOME: {
    title: 'Home',
    description: 'Deceased is at home or residence',
    icon: '🏠',
  },
  MOSQUE: {
    title: 'Mosque',
    description: 'Deceased is at a mosque or Islamic center',
    icon: '🕌',
  },
  OTHER: {
    title: 'Other',
    description: 'Different location not listed above',
    icon: '📍',
  },
}

function QuickAssessmentStep({ onNext, onBack, initialData }: QuickAssessmentStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const quickAssessmentMutation = useQuickAssessment()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<QuickAssessmentFormData>({
    resolver: zodResolver(quickAssessmentSchema),
    defaultValues: initialData,
    mode: 'onChange',
  })

  const selectedUrgency = watch('urgencyLevel')
  const selectedLocationType = watch('locationType')

  const onSubmit = async (data: QuickAssessmentFormData) => {
    try {
      setIsSubmitting(true)
      const result = await quickAssessmentMutation.mutateAsync(data)
      onNext(result)
    } catch (error) {
      console.error('Quick assessment failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <span className="text-2xl">🤝</span>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900">
          Quick Assessment
        </h1>
        <p className="text-lg text-neutral-600 max-w-lg mx-auto">
          We understand this is a difficult time. Please answer a few questions to help us provide the most appropriate guidance and support for your needs.
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-2">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            1
          </div>
          <span className="ml-2 text-sm font-medium text-primary-600">Quick Assessment</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            2
          </div>
          <span className="ml-2 text-sm text-neutral-500">Package Selection</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            3
          </div>
          <span className="ml-2 text-sm text-neutral-500">Deceased Information</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            4
          </div>
          <span className="ml-2 text-sm text-neutral-500">Provider Matches</span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Urgency Level */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              1. How urgent are the funeral arrangements?
            </h2>
            <p className="text-neutral-600 mb-6">
              This helps us prioritize your needs and recommend appropriate timelines.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(urgencyDescriptions).map(([level, config]) => (
                <label
                  key={level}
                  className={`
                    relative cursor-pointer rounded-lg border-2 p-4 transition-all
                    ${selectedUrgency === level
                      ? `${config.color} border-current`
                      : 'border-neutral-200 hover:border-neutral-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    value={level}
                    {...register('urgencyLevel')}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-2xl mb-2">{config.icon}</div>
                    <div className="font-medium text-neutral-900">
                      {config.title}
                    </div>
                    <div className="text-sm text-neutral-600 mt-1">
                      {config.description}
                    </div>
                  </div>
                  {selectedUrgency === level && (
                    <div className="absolute top-2 right-2">
                      <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </label>
              ))}
            </div>
            {errors.urgencyLevel && (
              <p className="mt-2 text-sm text-red-600">{errors.urgencyLevel.message}</p>
            )}
          </div>
        </Card>

        {/* Location Type */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              2. Where is the deceased currently located?
            </h2>
            <p className="text-neutral-600 mb-6">
              This helps us arrange the appropriate transportation and prepare the necessary facilities.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(locationTypeDescriptions).map(([type, config]) => (
                <label
                  key={type}
                  className={`
                    relative cursor-pointer rounded-lg border-2 p-4 transition-all
                    ${selectedLocationType === type
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    value={type}
                    {...register('locationType')}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-2xl mb-2">{config.icon}</div>
                    <div className="font-medium text-neutral-900 text-sm">
                      {config.title}
                    </div>
                    <div className="text-xs text-neutral-600 mt-1">
                      {config.description}
                    </div>
                  </div>
                  {selectedLocationType === type && (
                    <div className="absolute top-2 right-2">
                      <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </label>
              ))}
            </div>
            {errors.locationType && (
              <p className="mt-2 text-sm text-red-600">{errors.locationType.message}</p>
            )}
          </div>
        </Card>

        {/* Current Location */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              3. What is the specific address or location?
            </h2>
            <p className="text-neutral-600 mb-4">
              Please provide the complete address so we can arrange transportation.
            </p>

            <input
              type="text"
              {...register('currentLocation')}
              placeholder="e.g., Hospital Kuala Lumpur, Jalan Pahang, 50586 Kuala Lumpur"
              className={`
                w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                ${errors.currentLocation ? 'border-red-300' : 'border-neutral-300'}
              `}
            />
            {errors.currentLocation && (
              <p className="mt-2 text-sm text-red-600">{errors.currentLocation.message}</p>
            )}
          </div>
        </Card>

        {/* Preferred Burial Area */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              4. What is the preferred burial area?
            </h2>
            <p className="text-neutral-600 mb-4">
              This helps us find suitable burial grounds and Islamic facilities nearby.
            </p>

            <input
              type="text"
              {...register('preferredBurialArea')}
              placeholder="e.g., Ampang, Kuala Lumpur or Shah Alam, Selangor"
              className={`
                w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                ${errors.preferredBurialArea ? 'border-red-300' : 'border-neutral-300'}
              `}
            />
            {errors.preferredBurialArea && (
              <p className="mt-2 text-sm text-red-600">{errors.preferredBurialArea.message}</p>
            )}
          </div>
        </Card>

        {/* Islamic Guidelines Note */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Islamic Funeral Guidelines
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Our system follows Islamic funeral principles:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Quick burial (preferably within 24 hours)</li>
                  <li>Simple and dignified procedures</li>
                  <li>Respect for Islamic traditions</li>
                  <li>Coordination with local mosques and Islamic authorities</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between">
          {onBack && (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
            >
              Previous
            </Button>
          )}
          <div className="flex-1"></div>
          <Button
            type="submit"
            variant="islamic"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            className="min-w-40"
          >
            {isSubmitting ? 'Getting Recommendations...' : 'Continue to Package Selection'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default QuickAssessmentStep